import defaults from '../../../translations/en.json';

const { Missing } = defaults;

/**
 * Map ordering physician id (person number) → physician person id when any row has both.
 */
function buildOrderingIdToPersonId(compiledApprovals) {
  const map = new Map();
  if (!compiledApprovals) return map;
  compiledApprovals.forEach((row) => {
    const personId = row.physicianPersonId;
    const orderingId = row.orderingphysicianid;
    if (
      personId != null &&
      personId !== Missing &&
      orderingId != null &&
      orderingId !== Missing
    ) {
      map.set(String(orderingId), personId);
    }
  });
  return map;
}

/**
 * Single canonical key per physician: prefer person id; otherwise resolve ordering id → person id; else ordering id.
 */
export function canonicalPhysicianId(row, orderingToPersonId) {
  const personId = row.physicianPersonId;
  if (personId != null && personId !== Missing) return personId;

  const orderingId = row.orderingphysicianid;
  if (orderingId == null || orderingId === Missing) return null;

  const resolved = orderingToPersonId.get(String(orderingId));
  if (resolved != null && resolved !== Missing) return resolved;

  return orderingId;
}

function rowMatchesPhysicianId(row, physicianId, orderingToPersonId) {
  const id = canonicalPhysicianId(row, orderingToPersonId);
  if (id == null || physicianId == null) return false;
  return String(id) === String(physicianId);
}

/**
 * Unique physicians for the nurse Unsigned Verbals picker (one row per canonical physician id).
 */
export function uniquePhysiciansFromCompiledApprovals(compiledApprovals) {
  if (!compiledApprovals || !compiledApprovals.length) return [];

  const orderingToPersonId = buildOrderingIdToPersonId(compiledApprovals);
  const byKey = new Map();

  compiledApprovals.forEach((row) => {
    const id = canonicalPhysicianId(row, orderingToPersonId);
    if (id == null || row.doctorName === Missing) return;

    const mapKey = String(id);
    if (!byKey.has(mapKey)) {
      byKey.set(mapKey, { physicianKey: id, doctorName: row.doctorName });
    }
  });

  const filtered = Array.from(byKey.values()).filter(
    (p) => p.doctorName && p.doctorName.trim().length > 0,
  );

  return filtered.sort((a, b) => a.doctorName.localeCompare(b.doctorName));
}

/**
 * All raw ID forms (physicianpersonid / physicianpersonnumber) that refer to
 * the given canonical physician key. Used to filter a raw VA list where the
 * canonical key might have been resolved via the orderingToPersonId map, meaning
 * some raw approvals only carry the person-number form (physicianpersonid = null).
 */
export function physicianIdSet(compiledApprovals, physicianKey) {
  const ids = new Set([String(physicianKey)]);
  if (!compiledApprovals) return ids;

  const orderingToPersonId = buildOrderingIdToPersonId(compiledApprovals);

  compiledApprovals.forEach((row) => {
    if (!rowMatchesPhysicianId(row, physicianKey, orderingToPersonId)) return;
    const personId = row.physicianPersonId;
    const orderingId = row.orderingphysicianid;
    if (personId != null && personId !== Missing) ids.add(String(personId));
    if (orderingId != null && orderingId !== Missing)
      ids.add(String(orderingId));
  });

  return ids;
}

export function patientNumbersForPhysician(compiledApprovals, physicianKey) {
  if (!compiledApprovals) return [];

  const orderingToPersonId = buildOrderingIdToPersonId(compiledApprovals);
  const set = new Set();
  compiledApprovals.forEach((row) => {
    if (rowMatchesPhysicianId(row, physicianKey, orderingToPersonId)) {
      if (row.patientNumber) set.add(row.patientNumber);
    }
  });
  return Array.from(set);
}
